import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Papa from 'papaparse';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import useApi from '../../../hooks/useApi';
import ConfirmDialog from '../../ConfirmDialog';
import { useDetailDrawer } from '../../dashboard/useDetailDrawer';
import ReportActionStatusDialog from './ReportActionStatusDialog';
import ReportActionsBar from './ReportActionsBar';
import ReportDataGrid from './ReportDataGrid';
import ReportFiltersPanel from './ReportFiltersPanel';
import LegacyReportLayout from './LegacyReportLayout';
import ReportModals from './ReportModals';
import ReportPanels from './ReportPanels';
import ReportRecomputeControls, { supportsManualRecompute } from './ReportRecomputeControls';
import ReportRuntimeStatusPanel from './ReportRuntimeStatusPanel';
import ReportWidgetsGrid from './ReportWidgetsGrid';
import {
  buildDatasetMap,
  buildDetailRowCandidate,
  formatRowsForOutput,
  resolveInitialSortModel,
  scopeColor,
} from './reportViewUtils';
import useEmailSendManualAction from './useEmailSendManualAction.jsx';
import useForceTriggerOverride from './useForceTriggerOverride.jsx';
import { toManualExecutionDisplayStatus } from './emailTriggerDisplayStatus';
import { patchReportQueryRow } from './reportQueryCache';
import { isTerminalRefreshStatus } from './reportRefreshStatus';
import useReportActionStatusFlow from './useReportActionStatusFlow';
import useReportRefreshRuntimeState from './useReportRefreshRuntimeState';
import { isEmailRowActionAllowed } from './reportEmailActionEligibility';

// Report email scheduling UI pulls in heavier deps; keep it out of the main report-view chunk.
const ReportEmailSchedulePanel = lazy(() => import('../email-schedule/ReportEmailSchedulePanel'));

const getRowValue = (row, field) => {
  if (!row || !field) return undefined;
  const key = String(field);
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  if (!key.includes('.')) return row[key];
  return key
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .reduce((acc, segment) => (acc == null ? acc : acc[segment]), row);
};

const resolveServerErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  return (
    data.message ||
    data.reason ||
    data.error ||
    data.details?.message ||
    data.details?.reason ||
    fallback
  );
};

const getReportRowId = (row) =>
  row?.__rowId || row?.id || row?.rowId || row?.bac || row?.__fallbackId || JSON.stringify(row);

const ReportViewShell = ({ reportId }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompactDashboard = useMediaQuery(theme.breakpoints.down('md'));
  const isDetailFullScreen = isCompactDashboard;
  const appBarOffset = isSmallScreen ? 56 : 64;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    useReport,
    useReportDatasets,
    useReportQuery,
    useReportExport,
    useReportExportStatus,
    useReportRefresh,
    useReportRefreshStatus,
    useReportAction,
    useRunCronJob,
    useDeleteVmtSendHistory,
    useDeleteEmailTriggerSend,
    useRetryEmailTriggerSend,
    useSendManualEmailTrigger,
  } = useApi();
  const { data, isLoading, error } = useReport(reportId);
  const { data: datasetsData } = useReportDatasets();
  const [activeModalId, setActiveModalId] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [runtimeFilters, setRuntimeFilters] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [cachedRows, setCachedRows] = useState([]);
  const [lastTotal, setLastTotal] = useState(0);
  const [exportId, setExportId] = useState(null);
  const [exportPollInterval, setExportPollInterval] = useState(false);
  const [refreshPollInterval, setRefreshPollInterval] = useState(false);
  const [emailEvidenceCursor, setEmailEvidenceCursor] = useState(null);
  const [confirmState, setConfirmState] = useState({
    open: false,
    action: null,
    row: null,
    loading: false,
  });

  const reportExport = useReportExport();
  const reportRefresh = useReportRefresh();
  const reportAction = useReportAction();
  const runCronJob = useRunCronJob();
  const deleteSendHistory = useDeleteVmtSendHistory();
  const deleteEmailSend = useDeleteEmailTriggerSend();
  const retryEmailSend = useRetryEmailTriggerSend();
  const sendManualEmail = useSendManualEmailTrigger();
  const datasetMap = useMemo(() => buildDatasetMap(datasetsData?.datasets || []), [datasetsData]);
  const metadata = useMemo(() => data?.metadata || {}, [data]);
  const definition = useMemo(() => data?.definition || {}, [data]);
  const panels = useMemo(
    () => (Array.isArray(definition.extensions?.panels)
      ? definition.extensions.panels
      : []),
    [definition]
  );
  const hasEmailEvidencePanel = panels.some(
    (panel) =>
      panel?.type === 'email_notification_evidence' &&
      panel?.params?.dataset === 'email_sends' &&
      Number(panel?.params?.contractVersion) === 1
  );
  const dataset = datasetMap.get(definition.baseDataset) || null;
  const isEmailSendsReport = definition.baseDataset === 'email_sends';
  const defaultSortField = definition?.defaultSort?.field || null;
  const defaultSortDirection = definition?.defaultSort?.direction || definition?.defaultSort?.sort || null;
  const initialSortModel = useMemo(
    () => resolveInitialSortModel({
      defaultSort: defaultSortField
        ? { field: defaultSortField, sort: defaultSortDirection || 'desc' }
        : null,
    }),
    [defaultSortField, defaultSortDirection]
  );

  useEffect(() => {
    setSortModel(initialSortModel);
  }, [reportId, defaultSortField, defaultSortDirection, initialSortModel]);
  const normalizeDownloadUrl = (downloadUrl) => {
    if (!downloadUrl) return null;
    if (/^https?:\/\//i.test(downloadUrl)) return downloadUrl;
    const withSlash = downloadUrl.startsWith('/') ? downloadUrl : `/${downloadUrl}`;
    return `${window.location.origin}${withSlash}`;
  };

  const buildFallbackFileName = (status) => {
    const exportKey = status?.exportId || exportId || 'download';
    const format = status?.format || 'csv';
    return `report-export-${exportKey}.${format}`;
  };

  const handleDownloadExport = async () => {
    const downloadUrl = normalizeDownloadUrl(exportStatus.data?.downloadUrl);
    if (!downloadUrl) return;
    try {
      const response = await fetch(downloadUrl, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`download_failed_${response.status}`);
      }
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition') || '';
      const match = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
      const fileName = match ? decodeURIComponent(match[1]) : buildFallbackFileName(exportStatus.data);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      showSnackbar('Download failed. Please try again.', 'error');
    }
  };
  const queryParams = useMemo(
    () => ({
      filters: runtimeFilters,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      sort: sortModel[0] || null,
      includeMetrics: true,
      ...(hasEmailEvidencePanel
        ? {
            emailEvidenceCursor,
            emailEvidenceLimit: 25,
          }
        : {}),
    }),
    [
      runtimeFilters,
      paginationModel,
      sortModel,
      hasEmailEvidencePanel,
      emailEvidenceCursor,
    ]
  );

  useEffect(() => {
    setEmailEvidenceCursor(null);
  }, [reportId, runtimeFilters]);

  const reportQuery = useReportQuery(reportId, queryParams);
  const showSnackbar = useCallback(
    (message, severity = 'info') => setSnackbar({ open: true, message, severity }),
    []
  );
  const {
    actionStatusState,
    closeActionStatus,
    clearActionStatusSelection,
    selectActionStatusRows,
    runReportSendAction,
  } = useReportActionStatusFlow({
    reportId,
    reportAction,
    queryClient,
    setSelectionModel,
    showSnackbar,
    resolveServerErrorMessage,
  });
  const exportStatus = useReportExportStatus(exportId, { pollInterval: exportPollInterval });
  const refreshStatus = useReportRefreshStatus(reportId, { pollInterval: refreshPollInterval });
  const { effectiveRefreshStatus, lastRefreshCompletedAt, setOptimisticRefreshStatus } =
    useReportRefreshRuntimeState({
      reportId,
      refreshStatusData: refreshStatus.data,
    });
  const manualEmailAction = useEmailSendManualAction({
    enabled: isEmailSendsReport,
    onSent: () => {
      reportQuery.refetch();
      showSnackbar('Manual email sent.', 'success');
    },
  });
  const {
    applyOverrides: applyForceTriggerOverrides,
    handleRowAction: handleForceTriggerAction,
    dialog: forceTriggerDialog,
  } = useForceTriggerOverride();

  const baseRows = useMemo(
    () =>
      (Array.isArray(reportQuery.data?.rows) ? reportQuery.data.rows : []).map((row, index) => ({
        __rowId: row.__rowId || row.id || row.rowId || `row-${index}`,
        ...row,
      })),
    [reportQuery.data]
  );
  const formattedRows = useMemo(
    () => applyForceTriggerOverrides(baseRows),
    [baseRows, applyForceTriggerOverrides]
  );
  const hasQueryRows = reportQuery.data && Array.isArray(reportQuery.data.rows);
  const queryRows = hasQueryRows ? formattedRows : cachedRows;
  const queryTotal = Number.isFinite(reportQuery.data?.total) ? reportQuery.data.total : lastTotal;
  const queryWarnings = Array.isArray(reportQuery.data?.warnings) ? reportQuery.data.warnings : [];
  const metricWarnings = Array.isArray(reportQuery.data?.metricWarnings)
    ? reportQuery.data.metricWarnings
    : [];
  const metrics = reportQuery.data?.metrics || {};
  const hiddenFields = useMemo(
    () => (Array.isArray(definition.hiddenFields) ? definition.hiddenFields : []),
    [definition]
  );
  const visibleFields = useMemo(() => {
    const fields = Array.isArray(definition.fields) ? definition.fields : [];
    if (!hiddenFields.length) return fields;
    const hiddenSet = new Set(hiddenFields);
    return fields.filter((field) => !hiddenSet.has(field));
  }, [definition, hiddenFields]);
  const selectedRows = useMemo(() => {
    if (!Array.isArray(selectionModel) || !selectionModel.length) return [];
    const selected = new Set(selectionModel.map((value) => String(value)));
    return queryRows.filter((row) => selected.has(String(getReportRowId(row))));
  }, [selectionModel, queryRows]);

  const widgets = useMemo(() => {
    const widgetList = Array.isArray(definition.extensions?.widgets) ? definition.extensions.widgets : [];
    const widgetMap = new Map(widgetList.map((widget) => [widget.id, widget]));
    const dashboardOrder = Array.isArray(definition.dashboard?.widgets) ? definition.dashboard.widgets : [];
    if (dashboardOrder.length) {
      return dashboardOrder.map((id) => widgetMap.get(id)).filter(Boolean);
    }
    return widgetList;
  }, [definition]);
  const disableDefaults = useMemo(
    () => new Set(Array.isArray(definition.disableDefaults) ? definition.disableDefaults : []),
    [definition]
  );
  const legacyWidgets = useMemo(
    () => widgets.filter((widget) => widget?.type === 'legacy_report' || widget?.type === 'appsheet_table'),
    [widgets]
  );
  const standardWidgets = useMemo(
    () => widgets.filter((widget) => widget?.type !== 'legacy_report' && widget?.type !== 'appsheet_table'),
    [widgets]
  );
  const actions = useMemo(() => {
    const disableDefaults = Array.isArray(definition.disableDefaults) ? definition.disableDefaults : [];
    const exportBusy =
      reportExport.isPending || ['queued', 'running'].includes(exportStatus.data?.status);
    const refreshBusy =
      reportRefresh.isPending || ['queued', 'running'].includes(effectiveRefreshStatus?.status);
    const defaults = [
      { type: 'export_csv', params: { label: 'Export CSV' }, source: 'default', disabled: exportBusy },
      { type: 'refresh', params: { label: 'Refresh' }, source: 'default', disabled: refreshBusy },
      {
        type: 'clear_filters',
        params: { label: 'Clear filters' },
        source: 'default',
        disabled: runtimeFilters.length === 0,
      },
    ].filter((action) => !disableDefaults.includes(action.type));
    const extensions = Array.isArray(definition.extensions?.actions) ? definition.extensions.actions : [];
    const merged = [...defaults, ...extensions.map((action) => ({ ...action, source: 'extension' }))];
    const selectedCount = selectedRows.length;
    const withState = merged.map((action) => {
      if (
        ['sync_to_di', 'push_to_vmt'].includes(action?.type) &&
        action?.params?.scope === 'selected'
      ) {
        return { ...action, disabled: selectedCount === 0 || action.disabled };
      }
      return action;
    });
    if (manualEmailAction.action && !disableDefaults.includes('email_send_manual')) {
      withState.unshift(manualEmailAction.action);
    }
    return withState;
  }, [
    definition,
    reportExport.isPending,
    exportStatus.data?.status,
    reportRefresh.isPending,
    effectiveRefreshStatus?.status,
    runtimeFilters.length,
    manualEmailAction.action,
    selectedRows.length,
  ]);
  const modals = Array.isArray(definition.extensions?.modals) ? definition.extensions.modals : [];
  const rowActions = useMemo(() => {
    const baseRowActions = Array.isArray(definition.extensions?.rowActions) ? definition.extensions.rowActions : [];
    const hasForceEdit = baseRowActions.some((action) => action?.type === 'force_edit_trigger');
    if (hasForceEdit) return baseRowActions;
    const fields = Array.isArray(definition.fields) ? definition.fields : [];
    if (!fields.includes('emailTriggerId')) return baseRowActions;
    return [
      ...baseRowActions,
      {
        type: 'force_edit_trigger',
        params: {
          label: 'Force Edit Trigger',
          bacField: 'bac',
          programField: 'programName',
          requiredFields: ['bac', 'programName'],
        },
      },
    ];
  }, [definition]);
  const filters = Array.isArray(definition.filters) ? definition.filters : [];
  const showManualRecompute = supportsManualRecompute(dataset);
  const showActionsBar = !disableDefaults.has('actions_bar');
  const showWidgetsPanel = !disableDefaults.has('widgets_panel');
  const showFiltersPanel = !disableDefaults.has('filters_panel');
  const showDataPreview = !disableDefaults.has('data_preview');
  const navigateToDealer = (bac, options = {}) => {
    if (!bac) return;
    navigate(`/dealer/${encodeURIComponent(bac)}`, { state: options });
  };
  const { detailDrawer, openDetail } = useDetailDrawer({
    filteredData: queryRows,
    navigateToDealer,
    onRowUpdated: (updatedRow) => {
      if (!updatedRow) return;
      patchReportQueryRow({
        queryClient,
        reportId,
        row: updatedRow,
        patch: updatedRow,
      });
    },
    isDetailFullScreen,
    appBarOffset,
  });

  useEffect(() => {
    const status = exportStatus.data?.status;
    if (status === 'completed' || status === 'failed' || status === 'expired') {
      setExportPollInterval(false);
    }
  }, [exportStatus.data]);

  useEffect(() => {
    if (!isTerminalRefreshStatus(effectiveRefreshStatus?.status)) return;
    setRefreshPollInterval(false);
    reportQuery.refetch();
  }, [effectiveRefreshStatus?.status, reportQuery, setOptimisticRefreshStatus]);

  useEffect(() => {
    if (!hasQueryRows) return;
    setCachedRows(formattedRows);
  }, [formattedRows, hasQueryRows]);

  useEffect(() => {
    if (Number.isFinite(reportQuery.data?.total)) {
      setLastTotal(reportQuery.data.total);
    }
  }, [reportQuery.data?.total]);

  useEffect(() => {
    setCachedRows([]);
    setLastTotal(0);
    setSelectionModel([]);
    setOptimisticRefreshStatus(null);
  }, [reportId, setOptimisticRefreshStatus]);
  useEffect(() => {
    setSelectionModel((prev) => {
      if (!prev.length) return prev;
      const valid = new Set(queryRows.map((row) => String(getReportRowId(row))));
      const next = prev.filter((id) => valid.has(String(id)));
      return next.length === prev.length ? prev : next;
    });
  }, [queryRows]);

  const queueReportRefresh = async ({ recompute = { level: 'report' }, successMessage = 'Refresh queued.' } = {}) => {
    try {
      const response = await reportRefresh.mutateAsync({ reportId, reason: 'manual', recompute });
      setOptimisticRefreshStatus({
        refreshId: response?.refreshId || null,
        reportId,
        status: response?.status || 'queued',
        completedAt: null,
      });
      setRefreshPollInterval(2000);
      try {
        await refreshStatus.refetch();
      } catch (_refetchError) {
        // Status polling handles the eventual refresh state; this best-effort refetch should not block queueing.
      }
      showSnackbar(successMessage, 'success');
    } catch (_error) {
      setOptimisticRefreshStatus(null);
      showSnackbar(resolveServerErrorMessage(_error, 'Failed to refresh report.'), 'error');
    }
  };

  const executeAction = async (action) => {
    if (action.type === 'open_modal' && action.params?.modalId) {
      setActiveModalId(action.params.modalId);
      return;
    }
    if (action.type === 'open_report' && action.params?.reportId) {
      navigate(`/reports/${action.params.reportId}`);
      return;
    }
    if (action.type === 'export_csv') {
      try {
        const response = await reportExport.mutateAsync({
          reportId,
          format: 'csv',
          filters: runtimeFilters,
        });
        setExportId(response.exportId);
        setExportPollInterval(2000);
        showSnackbar('Export queued.', 'success');
      } catch (_error) {
        showSnackbar('Failed to queue export.', 'error');
      }
      return;
    }
    if (action.type === 'export_json') {
      try {
        const response = await reportExport.mutateAsync({
          reportId,
          format: 'json',
          filters: runtimeFilters,
        });
        setExportId(response.exportId);
        setExportPollInterval(2000);
        showSnackbar('Export queued.', 'success');
      } catch (_error) {
        showSnackbar('Failed to queue export.', 'error');
      }
      return;
    }
    if (action.type === 'copy_csv') {
      try {
        const payloadRows = formatRowsForOutput(queryRows, visibleFields);
        const csv = Papa.unparse(payloadRows);
        await navigator.clipboard.writeText(csv);
        showSnackbar('Copied CSV to clipboard.', 'success');
      } catch (_error) {
        showSnackbar('Failed to copy CSV.', 'error');
      }
      return;
    }
    if (action.type === 'copy_json') {
      try {
        const payloadRows = formatRowsForOutput(queryRows, visibleFields);
        await navigator.clipboard.writeText(JSON.stringify(payloadRows, null, 2));
        showSnackbar('Copied JSON to clipboard.', 'success');
      } catch (_error) {
        showSnackbar('Failed to copy JSON.', 'error');
      }
      return;
    }
    if (action.type === 'run_job' && action.params?.job) {
      try {
        await runCronJob.mutateAsync(action.params.job);
        showSnackbar(`Job ${action.params.job} queued.`, 'success');
      } catch (_error) {
        showSnackbar('Failed to queue job.', 'error');
      }
      return;
    }
    if (action.type === 'refresh') {
      await queueReportRefresh({ recompute: { level: 'report' } });
      return;
    }
    if (action.type === 'sync_to_di' || action.type === 'push_to_vmt') {
      const scope = action.params?.scope || 'visible';
      const rows = scope === 'selected' ? selectedRows : queryRows;
      await runReportSendAction({ action, rows, actionScope: 'selected', clearSelection: true });
      return;
    }
    if (manualEmailAction.handleAction(action)) {
      return;
    }
    if (action.type === 'clear_filters') {
      setRuntimeFilters([]);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      showSnackbar('Filters cleared.', 'info');
    }
  };

  const executeRowAction = async (action, row) => {
    if (!isEmailRowActionAllowed(row, action?.type)) {
      showSnackbar('This report-only status observation cannot trigger email.', 'info');
      return;
    }
    if (action.type === 'open_detail_panel') {
      const detailRow = buildDetailRowCandidate(row, action.params || {});
      if (!detailRow?.bac || !(detailRow?.programName || detailRow?.programCode || detailRow?.program)) {
        showSnackbar('Missing BAC or program for details view.', 'warning');
        return;
      }
      openDetail(detailRow);
      return;
    }
    if (action.type === 'open_modal' && action.params?.modalId) {
      setActiveRow(row);
      setActiveModalId(action.params.modalId);
      return;
    }
    if (action.type === 'run_job' && action.params?.job) {
      try {
        await runCronJob.mutateAsync(action.params.job);
        showSnackbar(`Job ${action.params.job} queued.`, 'success');
      } catch (_error) {
        showSnackbar('Failed to queue job.', 'error');
      }
    }
    if (action.type === 'send_history_delete') {
      if (!row?.id) return;
      try {
        await deleteSendHistory.mutateAsync([row.id]);
        await reportQuery.refetch();
        showSnackbar('Send history entry deleted.', 'success');
      } catch (_error) {
        showSnackbar('Failed to delete send history entry.', 'error');
      }
    }
    if (action.type === 'email_send_delete') {
      if (!row?.id) return;
      try {
        await deleteEmailSend.mutateAsync(row.id);
        await reportQuery.refetch();
        showSnackbar('Email send entry deleted.', 'success');
      } catch (_error) {
        showSnackbar('Failed to delete email send entry.', 'error');
      }
    }
    if (action.type === 'email_send_retry') {
      if (!row?.id) return;
      try {
        await retryEmailSend.mutateAsync({ id: row.id, options: { forceSend: true } });
        await reportQuery.refetch();
        showSnackbar('Email send retry queued.', 'success');
      } catch (_error) {
        showSnackbar('Failed to retry email send.', 'error');
      }
    }
    if (action.type === 'execute_trigger') {
      try {
        if (row?.emailTriggerOverride && row?.emailTriggerId) {
          const params = action.params || {};
          const bacField = params.bacField || 'bac';
          const programField = params.programField || 'programName';
          const bac = getRowValue(row, bacField);
          const programName = getRowValue(row, programField);
          if (!bac || !programName) {
            showSnackbar('Missing BAC or program for manual send.', 'warning');
            return;
          }
          const result = await sendManualEmail.mutateAsync({
            bac,
            programName,
            triggerId: row.emailTriggerId,
          });
          patchReportQueryRow({
            queryClient,
            reportId,
            row,
            patch: { emailTriggerStatus: toManualExecutionDisplayStatus(result) },
          });
          showSnackbar('Manual trigger sent.', 'success');
          return;
        }
        const result = await reportAction.mutateAsync({ reportId, actionType: action.type, row });
        patchReportQueryRow({
          queryClient,
          reportId,
          row,
          patch: { emailTriggerStatus: toManualExecutionDisplayStatus(result) },
        });
        showSnackbar('Trigger executed.', 'success');
      } catch (_error) {
        showSnackbar(resolveServerErrorMessage(_error, 'Failed to execute trigger.'), 'error');
      }
      return;
    }
    if (action.type === 'remove_row') {
      try {
        await reportAction.mutateAsync({ reportId, actionType: action.type, action, row });
        patchReportQueryRow({
          queryClient,
          reportId,
          row,
          patch: { __removed: true },
        });
        setSnackbar({ open: true, message: 'Row removed from the unmatched report.', severity: 'success' });
        await reportQuery.refetch();
      } catch (_error) {
        setSnackbar({ open: true, message: resolveServerErrorMessage(_error, 'Failed to remove row.'), severity: 'error' });
      }
      return;
    }
    if (action.type === 'sync_to_di' || action.type === 'push_to_vmt') {
      await runReportSendAction({ action, row, actionScope: 'row' });
      return;
    }
  };

  const handleAction = (action) => {
    if (action.params?.confirm) {
      setConfirmState({ open: true, action, row: null, loading: false });
      return;
    }
    executeAction(action);
  };

  const handleRowAction = (action, row) => {
    if (handleForceTriggerAction(action, row)) return;
    if (action.params?.confirm) {
      setConfirmState({ open: true, action, row, loading: false });
      return;
    }
    executeRowAction(action, row);
  };

  const handleConfirm = async () => {
    if (!confirmState.action) return;
    setConfirmState((prev) => ({ ...prev, loading: true }));
    try {
      if (confirmState.row) {
        await executeRowAction(confirmState.action, confirmState.row);
      } else {
        await executeAction(confirmState.action);
      }
    } finally {
      setConfirmState({ open: false, action: null, row: null, loading: false });
    }
  };

  const handleAddFilter = (filter) => {
    if (!filter?.field || !filter?.op) return;
    setRuntimeFilters((prev) => [...prev, { field: filter.field, op: filter.op, value: filter.value }]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRemoveFilter = (index) => {
    setRuntimeFilters((prev) => prev.filter((_, i) => i !== index));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setRuntimeFilters([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  if (isLoading) {
    return (
      <Alert severity="info">Loading report definition...</Alert>
    );
  }

  if (error) {
    return <Alert severity="error">{error?.message || 'Failed to load report.'}</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack spacing={1}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {metadata.name || 'Report'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {metadata.description || 'No description provided.'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip label={metadata.status || 'draft'} variant="outlined" />
            <Chip label={definition.scope || 'scope'} color={scopeColor(definition.scope)} />
            <Chip label={dataset?.label || definition.baseDataset || 'dataset'} variant="outlined" />
            <Chip label={`v${data?.version || 1}`} variant="outlined" />
          </Stack>
          {Array.isArray(metadata.tags) && metadata.tags.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {metadata.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" />
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      {showActionsBar && (
        <ReportActionsBar
          actions={actions}
          onAction={handleAction}
          loadingActionTypes={['queued', 'running'].includes(effectiveRefreshStatus?.status) ? ['refresh'] : []}
        />
      )}

      {showManualRecompute && (
        <ReportRecomputeControls
          dataset={dataset}
          selectedRows={selectedRows}
          busy={reportRefresh.isPending || ['queued', 'running'].includes(effectiveRefreshStatus?.status)}
          onRecompute={(recompute) => queueReportRefresh({ recompute, successMessage: 'Report recompute queued.' })}
        />
      )}

      <ReportRuntimeStatusPanel
        exportStatus={exportStatus.data}
        refreshStatus={effectiveRefreshStatus}
        lastRefreshedAt={lastRefreshCompletedAt}
        onDownload={handleDownloadExport}
      />

      {legacyWidgets.length > 0 && <LegacyReportLayout widgets={legacyWidgets} />}

      {showWidgetsPanel && <ReportWidgetsGrid widgets={standardWidgets} metrics={metrics} rows={queryRows} />}

      {showFiltersPanel && (
        <ReportFiltersPanel
          dataset={dataset}
          filters={filters}
          runtimeFilters={runtimeFilters}
          onAddFilter={handleAddFilter}
          onRemoveFilter={handleRemoveFilter}
          onClearFilters={handleClearFilters}
        />
      )}

      {(queryWarnings.length > 0 || metricWarnings.length > 0) && (
        <Alert severity="warning">
          {[...queryWarnings, ...metricWarnings].map((warning, index) => (
            <Typography key={`${warning.code || 'warning'}-${index}`} variant="body2">
              {warning.message || warning.code}
            </Typography>
          ))}
        </Alert>
      )}

      {showDataPreview && (
        <ReportDataGrid
          rows={queryRows}
          fields={Array.isArray(definition.fields) ? definition.fields : []}
          hiddenFields={hiddenFields}
          rowActions={rowActions}
          onRowAction={handleRowAction}
          loading={reportQuery.isLoading}
          total={queryTotal}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          selectionModel={selectionModel}
          onSelectionModelChange={setSelectionModel}
          enableRowSelection={actions.some((action) =>
            ['sync_to_di', 'push_to_vmt'].includes(action?.type) && action?.params?.scope === 'selected'
          ) || showManualRecompute}
        />
      )}

      <ReportPanels
        panels={panels}
        reportId={reportId}
        runtimeFilters={runtimeFilters}
        emailNotificationEvidence={
          reportQuery.data?.emailNotificationEvidence
        }
        loading={reportQuery.isLoading}
        onEmailEvidenceNextPage={setEmailEvidenceCursor}
        onEmailEvidenceError={(message) =>
          showSnackbar(message, 'error')
        }
      />

      <Suspense fallback={null}>
        <ReportEmailSchedulePanel reportId={reportId} />
      </Suspense>

      <ReportModals
        activeModalId={activeModalId}
        modals={modals}
        metadata={metadata}
        definition={definition}
        row={activeRow}
        onClose={() => setActiveModalId(null)}
        onAction={handleRowAction}
        actionState={{
          emailRetryPending: retryEmailSend.isPending,
        }}
      />

      {forceTriggerDialog}
      {manualEmailAction.dialog}

      <ReportActionStatusDialog
        open={actionStatusState.open}
        onClose={closeActionStatus}
        isMobile={isSmallScreen}
        actionType={actionStatusState.actionType}
        rows={actionStatusState.rows}
        inFlight={actionStatusState.inFlight}
        recompute={actionStatusState.recompute}
        onClearSelection={clearActionStatusSelection}
        onSelectFailedRows={selectActionStatusRows}
      />

      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.action?.params?.confirm?.title || 'Confirm action'}
        description={confirmState.action?.params?.confirm?.message || 'Proceed with this action?'}
        confirmLabel={confirmState.action?.params?.confirm?.confirmLabel || 'Confirm'}
        confirmColor={confirmState.action?.params?.confirm?.confirmColor || 'primary'}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmState({ open: false, action: null, row: null, loading: false })}
        loading={confirmState.loading}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity || 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {detailDrawer}
    </Box>
  );
};

export default ReportViewShell;
